import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bubble } from '../components/Bubble';
import { LEADERBOARD_ROW_EXTENT, LeaderboardRow } from '../components/LeaderboardRow';
import { type BoardDef, type BoardId, LEADERBOARDS, type LeaderboardEntry, scoreLabel } from '../models/types';
import { boardState, meIndex, useLeaderboard } from '../state/leaderboard';
import { usePlayer } from '../state/player';
import { useShell } from '../state/shell';

/** Distance from either end of the list at which the next page is requested. */
const LOAD_THRESHOLD = 200;

/** The Ranks tab: one tab per board, each a list anchored on the signed-in player that grows in both directions as they scroll. */
export function LeaderboardScreen() {
  const selected = useLeaderboard((s) => s.selected);
  // Boards are mounted the first time their tab is opened and then kept, hidden, so
  // flipping back keeps their scroll position and loaded pages.
  const [opened, setOpened] = useState<ReadonlySet<BoardId>>(() => new Set([selected]));
  useEffect(() => {
    setOpened((o) => (o.has(selected) ? o : new Set([...o, selected])));
  }, [selected]);

  return (
    <section className="screen" aria-label="Ranks">
      <h1 className="app-bar">Ranks</h1>
      <div className="board-tabs" role="tablist" aria-label="Leaderboards">
        {LEADERBOARDS.map((b) => (
          <button
            key={b.id}
            type="button"
            role="tab"
            id={`board-tab-${b.id}`}
            aria-selected={b.id === selected}
            aria-controls={`board-panel-${b.id}`}
            className="board-tab"
            onClick={() => useLeaderboard.getState().select(b.id)}
          >
            {b.label}
          </button>
        ))}
      </div>
      {LEADERBOARDS.filter((b) => opened.has(b.id) || b.id === selected).map((b) => (
        <BoardPanel key={b.id} board={b} active={b.id === selected} />
      ))}
    </section>
  );
}

function BoardPanel({ board, active }: { board: BoardDef; active: boolean }) {
  const id = board.id;
  const { data, error } = useLeaderboard((s) => boardState(s, id));
  const list = useRef<HTMLDivElement>(null);
  const prev = useRef<{ generation: number; firstId: string | null; height: number } | null>(null);

  useEffect(() => {
    if (!boardState(useLeaderboard.getState(), id).data) void useLeaderboard.getState().load(id);
  }, [id]);

  const maybeLoad = useCallback(() => {
    const el = list.current;
    if (!el || !active) return;
    if (el.scrollTop < LOAD_THRESHOLD) void useLeaderboard.getState().loadUp(id);
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_THRESHOLD) void useLeaderboard.getState().loadDown(id);
  }, [id, active]);

  // A reloaded window re-centres on the player; a prepended page keeps the rows on screen where they were.
  // A hidden board has no layout, so this waits until its tab is showing again.
  useLayoutEffect(() => {
    const el = list.current;
    if (!el || !data || !active) return;
    const first = data.entries[0]?.playerId ?? null;
    const before = prev.current;
    if (!before || before.generation !== data.generation) {
      // Put the player's row mid-screen, but never leave blank space above the first entry.
      const aboveExtent = meIndex(data) * LEADERBOARD_ROW_EXTENT;
      el.scrollTop = Math.max(0, aboveExtent - (el.clientHeight / 2 - LEADERBOARD_ROW_EXTENT / 2));
    } else if (before.firstId !== first) {
      el.scrollTop += el.scrollHeight - before.height;
    }
    prev.current = { generation: data.generation, firstId: first, height: el.scrollHeight };
    maybeLoad();
  }, [data, active, maybeLoad]);

  const open = (entry: LeaderboardEntry) => {
    const myId = usePlayer.getState().player?.profile.playerId;
    if (entry.isMe || entry.playerId === myId) {
      useShell.getState().select('you');
      return;
    }
    useShell.getState().openProfile({ playerId: entry.playerId, rank: entry.rank, displayName: entry.displayName });
  };

  return (
    <div className="board-panel" role="tabpanel" id={`board-panel-${id}`} aria-labelledby={`board-tab-${id}`} hidden={!active} data-testid={`board-${id}`}>
      {data ? (
        <>
          <div style={{ padding: '8px 16px 12px', flex: 'none' }}>
            <Bubble style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 32 }} aria-hidden="true">🏆</span>
              <div style={{ flex: 1 }}>
                <div className="t-title-l" style={{ fontWeight: 900 }}>#{data.me.rank} of {data.totalPlayers} hunters</div>
                <div>{scoreLabel(board, data.me.score, true)}</div>
              </div>
              <button type="button" className="icon-btn" aria-label="Refresh leaderboard" onClick={() => void useLeaderboard.getState().refresh(id)}>⟳</button>
            </Bubble>
          </div>
          <div className="lb-list" ref={list} onScroll={maybeLoad} style={{ overflowAnchor: 'none' }} data-testid="lb-list">
            {data.loadingUp && <EdgeSpinner top />}
            {data.entries.map((e) => (
              <LeaderboardRow key={e.playerId} entry={e} score={scoreLabel(board, e.score)} onClick={() => open(e)} />
            ))}
            {data.loadingDown && <EdgeSpinner />}
          </div>
        </>
      ) : error ? (
        <div className="center-fill">Could not load the leaderboard.<br />{error}</div>
      ) : (
        <div className="center-fill"><span className="spinner dark" role="progressbar" aria-label="Loading" /></div>
      )}
    </div>
  );
}

function EdgeSpinner({ top = false }: { top?: boolean }) {
  // Overlaid (not in flow) so showing it never shifts the rows under the reader's thumb.
  return (
    <div style={top ? { position: 'sticky', top: 4, height: 0, zIndex: 1 } : { position: 'sticky', bottom: 4, height: 0, zIndex: 1, marginTop: 8 }} role="progressbar" aria-label="Loading more">
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', [top ? 'top' : 'bottom']: 0 }}>
        <span className="spinner dark" style={{ width: 28, height: 28 }} />
      </div>
    </div>
  );
}
